import { useState } from "react";
import { useFetcher } from "react-router";
import type { EventFollow, SpaceEvent } from "@repo/shared-types";
import { Eye } from "lucide-react";
import type { FollowOutcome } from "@/routes/follow";
import { Modal } from "@/components/modal";
import { ErrorNote, ToggleChips } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { usePop } from "@/components/pop";

/**
 * Following an event (decided with Tiago 20 October): one tap follows it,
 * with comments, changes and new results all on; once followed, the same
 * control opens a sheet of what reaches the inbox — a chip each, saving on
 * the tap — and Unfollow. Drawn on the event's page as an eye (0077), and
 * on every card as an item of its "…" menu (0083), only for a published
 * event, which is all anybody follows.
 *
 * Each write goes to the event's follow route by fetcher, which toasts what
 * worked. The control draws what a write on its way asks for, then what it
 * answered, then what the page read: a card loaded by "Show more" is never
 * read again, and still shows the truth.
 */

type Choices = Pick<EventFollow, "comments" | "changes" | "results">;

export function useFollow(event: SpaceEvent, base: string) {
  const fetcher = useFetcher<FollowOutcome>();
  let follow: Choices | null = event.follow;
  if (fetcher.data && !fetcher.data.error) follow = fetcher.data.follow ?? null;
  const pending = fetcher.formData;
  if (pending?.get("intent") === "unfollow") follow = null;
  if (pending?.get("intent") === "follow") follow = { comments: true, changes: true, results: true };
  if (pending?.get("intent") === "choose") {
    follow = {
      comments: pending.get("comments") === "true",
      changes: pending.get("changes") === "true",
      results: pending.get("results") === "true",
    };
  }
  const send = (fields: Record<string, string>) =>
    fetcher.submit({ title: event.title, ...fields }, { method: "post", action: `${base}/events/${event.id}/follow` });
  return {
    follow,
    error: fetcher.data?.error,
    start: () => send({ intent: "follow" }),
    choose: (choices: Choices) =>
      send({
        intent: "choose",
        comments: String(choices.comments),
        changes: String(choices.changes),
        results: String(choices.results),
      }),
    stop: () => send({ intent: "unfollow" }),
  };
}

/**
 * The event page's eye, round and as tall as its bar's buttons, named Follow
 * or Following (0077, 0078): it follows at one tap, then opens the sheet. A
 * feed's card follows from its "…" menu instead (0083), through `useFollow`
 * and `FollowSheet`. A refusal shows under the bar, over the page, since the
 * row it sits in has no room.
 */
export function FollowControl({ event, base }: { event: SpaceEvent; base: string }) {
  const control = useFollow(event, base);
  const [open, setOpen] = useState(false);
  const pop = usePop();
  if (event.status !== "published") return null;
  const following = control.follow !== null;
  return (
    <div className="relative">
      {/* The eye is never filled, which would hide its pupil: the button's
          fill, the primary colour, says it is followed (0077). */}
      <Button
        type="button"
        variant={following ? "default" : "outline"}
        size="icon-lg"
        aria-label={following ? "Following" : "Follow"}
        onClick={() => {
          if (following) return setOpen(true);
          pop.play("eye");
          control.start();
        }}
      >
        {pop.wrap("eye", <Eye aria-hidden="true" />)}
      </Button>
      {control.error && !open && (
        <div className="absolute right-0 top-full z-10 mt-2 w-64">
          <ErrorNote message={control.error} />
        </div>
      )}
      {open && control.follow && (
        <FollowSheet event={event} control={control} follow={control.follow} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

const CHOICES: { name: keyof Choices; label: string; says: string }[] = [
  { name: "comments", label: "Comments", says: "Comments on the event, and replies to them." },
  {
    name: "changes",
    label: "Changes",
    says: "When it starts or ends, parts added or taken off, and deadlines moved.",
  },
  { name: "results", label: "New results", says: "Results posted on its leaderboards." },
];

/**
 * What of a followed event reaches the inbox, each chip saving all three on
 * the tap — so a tap made while the last is on its way still sends what the
 * chips show. With all three off it stays followed, on Home's Following, and
 * sends nothing. Unfollow closes the sheet.
 */
export function FollowSheet({
  event,
  control,
  follow,
  onClose,
}: {
  event: SpaceEvent;
  control: ReturnType<typeof useFollow>;
  follow: Choices;
  onClose: () => void;
}) {
  // A post only has comments to tell of (0090): its dates and parts never change.
  const choices = event.isPost ? CHOICES.filter((choice) => choice.name === "comments") : CHOICES;
  const quiet = choices.every((choice) => !follow[choice.name]);
  return (
    <Modal title="Following" onClose={onClose}>
      <p className="-mt-3 truncate text-sm text-muted-foreground">{event.title}</p>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Tell me about</p>
        <ToggleChips
          label="Tell me about"
          options={choices.map((choice) => ({
            name: choice.name,
            label: choice.label,
            says: choice.says,
            on: follow[choice.name],
            onChange: (on: boolean) => control.choose({ ...follow, [choice.name]: on }),
          }))}
        />
        {quiet && (
          <p className="text-xs text-muted-foreground">
            Nothing reaches your inbox, and it stays in Following on Home.
          </p>
        )}
        {control.error && <ErrorNote message={control.error} />}
      </div>
      <Separator />
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        onClick={() => {
          control.stop();
          onClose();
        }}
      >
        Unfollow
      </Button>
      <Button type="button" size="lg" className="w-full" onClick={onClose}>
        Done
      </Button>
    </Modal>
  );
}
